import {
  agentCapability,
  healthSample,
  hierarchyTree,
  nodeInventory,
  nodeStatus,
  nodeType,
  osFamily,
  type AgentCapability,
  type HealthSample,
  type HierarchyTree,
  type NodeInventory,
  type NodeStatus,
  type NodeType,
  type OsFamily,
} from '@occ/contracts';
import { request, type Query } from '../api/http';
import { ROUTES } from '../api/routes';

/**
 * Lecturas tipadas de la jerarquía y de los nodos.
 *
 * Conviven con el `dataProvider` de Refine a propósito: los hooks de Refine
 * devuelven `BaseRecord` y las pantallas de la Fase 1 necesitan los tipos
 * exactos del contrato (estado, salud, capacidades). El `dataProvider` sigue
 * ahí para lo que Refine resuelve solo — menú, `useCan`, CRUD futuro.
 */

/**
 * Fila de nodo tal como la devuelve el backend.
 *
 * No se valida con `nodeSummary` de `@occ/contracts` porque el backend declara
 * `osFamily` como nullable —un nodo enrolado que aún no reportó inventario no
 * sabe qué sistema tiene— y el esquema del contrato lo exige presente. El panel
 * acepta la realidad y pinta un guion; la discrepancia está anotada para que la
 * resuelva quien mantiene el contrato.
 */
export type NodeRow = {
  id: string;
  tenantId: string;
  branchId: string;
  alias: string;
  status: NodeStatus;
  lastSeenAt: Date | null;
  osFamily: OsFamily | null;
  agentVersion: string | null;
  ip: string | null;
  provider: string | null;
  monthlyCostCents: number | null;
  nodeType: NodeType | null;
  cpuAvgPercent: number | null;
  memAvgPercent: number | null;
};

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};

const asDate = (value: unknown): Date | null => {
  if (typeof value !== 'string' && !(value instanceof Date)) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const asNullableNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

export const parseNodeRow = (value: unknown): NodeRow => {
  const row = asRecord(value);
  return {
    id: String(row.id),
    tenantId: String(row.tenantId),
    branchId: String(row.branchId),
    alias: String(row.alias),
    status: nodeStatus.parse(row.status),
    lastSeenAt: asDate(row.lastSeenAt),
    osFamily:
      row.osFamily === null || row.osFamily === undefined ? null : osFamily.parse(row.osFamily),
    agentVersion: typeof row.agentVersion === 'string' ? row.agentVersion : null,
    ip: typeof row.ip === 'string' ? row.ip : null,
    provider: typeof row.provider === 'string' ? row.provider : null,
    monthlyCostCents: asNullableNumber(row.monthlyCostCents),
    nodeType:
      row.nodeType === null || row.nodeType === undefined ? null : nodeType.parse(row.nodeType),
    cpuAvgPercent: asNullableNumber(row.cpuAvgPercent),
    memAvgPercent: asNullableNumber(row.memAvgPercent),
  };
};

/** Las colecciones del backend son arrays desnudos; no hay envoltorio ni total. */
const asArray = (payload: unknown): unknown[] => (Array.isArray(payload) ? payload : []);

export const fetchHierarchyTree = (): Promise<HierarchyTree> =>
  request({ method: 'GET', path: ROUTES.hierarchyTree, schema: hierarchyTree });

export type NodeFilters = {
  clientId?: string;
  branchId?: string;
  status?: NodeStatus;
  search?: string;
};

export const fetchNodes = async (filters: NodeFilters = {}): Promise<NodeRow[]> => {
  const payload = await request<unknown>({
    method: 'GET',
    path: ROUTES.nodes,
    query: filters as Query,
  });
  return asArray(payload).map(parseNodeRow);
};

/**
 * Ficha de nodo.
 *
 * Dos fuentes porque el backend las expone por separado:
 *
 * - `GET /nodes/:id` da la fila guardada, que existe aunque el nodo esté apagado;
 * - la acción `nodes.get` da lo que solo se sabe preguntando al nodo ahora mismo
 *   —salud y capacidades declaradas—. Se pide por `POST /actions/nodes.get` y no
 *   por una ruta propia porque es la misma puerta que usa MCP (P3);
 * - la cadena cliente→sucursal la resuelve el árbol que ya tiene el panel.
 */
export type NodeDetail = {
  node: NodeRow;
  health: HealthSample | null;
  /**
   * Capacidades que el agente declaró en su `HELLO`. Vacío significa que no se
   * pudo preguntar —nodo apagado, broker sin responder, acción sin permiso—, y
   * entonces el panel no filtra por capacidad: esconder pestañas que sí
   * funcionarían cuando el nodo vuelva sería peor que dejar que la puerta decida.
   */
  capabilities: AgentCapability[];
};

export const fetchNodeDetail = async (id: string): Promise<NodeDetail> => {
  const payload = asRecord(await request<unknown>({ method: 'GET', path: ROUTES.node(id) }));
  const node = parseNodeRow(payload);

  // Salud y capacidades son información extra: si la acción no está disponible
  // todavía (501) o el usuario no alcanza, la ficha se pinta igual sin ellas. Se
  // llama en crudo y no con `runAction` porque solo interesan esos dos campos, y
  // validar el resto de la salida haría fallar la ficha por un nodo sin sistema
  // operativo reportado.
  const live = await request<unknown>({
    method: 'POST',
    path: ROUTES.action('nodes.get'),
    body: { node: { nodeId: id } },
  })
    .then((result) => asRecord(result))
    .catch(() => ({}) as Record<string, unknown>);

  const health = healthSample.safeParse(live.health);
  // Capacidad por capacidad y no de golpe: un agente más nuevo que este panel
  // puede declarar una que aquí no se conoce, y tirar la lista entera por ella
  // apagaría las cuatro pestañas de un nodo que funciona.
  const capabilities = Array.isArray(live.capabilities)
    ? live.capabilities.flatMap((value) => {
        const parsed = agentCapability.safeParse(value);
        return parsed.success ? [parsed.data] : [];
      })
    : [];

  return { node, health: health.success ? health.data : null, capabilities };
};

/**
 * Inventario. El backend devuelve el objeto tal cual lo reportó el agente, que
 * es `{}` si el nodo se enroló y todavía no ha mandado el primero.
 */
export const fetchNodeInventory = async (id: string): Promise<NodeInventory | null> => {
  const payload = await request<unknown>({ method: 'GET', path: ROUTES.nodeInventory(id) });
  const parsed = nodeInventory.safeParse(payload);
  return parsed.success ? parsed.data : null;
};

export const updateNodeAlias = async (id: string, alias: string): Promise<NodeRow> => {
  const payload = await request<unknown>({
    method: 'PATCH',
    path: ROUTES.node(id),
    body: { alias },
  });
  return parseNodeRow(payload);
};
